import type { ProjectId } from "@taskin-code/contracts";
import { Link } from "@tanstack/react-router";
import { AgentAvatar } from "~/components/AgentAvatar";
import { useVisibleManagedAgents } from "~/hooks/useProviderModelCatalog";
import { useStore } from "~/store";
import { BotIcon } from "~/lib/icons";
import { SidebarHeaderTrigger, SidebarInset } from "../ui/sidebar";
import { initiativesForProjectAgent } from "./ProjectAgentsView.logic";
import { filterCoordinators, projectAgentWarning } from "../settings/ProjectAgentsSection.logic";
import { useCreateInitiativeThread } from "~/hooks/useCreateInitiativeThread";
import { formatRelativeTime } from "~/lib/relativeTime";

export function ProjectAgentsView({ fixedProjectId }: { fixedProjectId: ProjectId }) {
  const project = useStore((s) => s.projects.find((p) => p.id === fixedProjectId));
  const threads = useStore((s) => s.threads);
  const agents = filterCoordinators(
    useVisibleManagedAgents().filter((agent) => project?.agentIds.includes(agent.id)),
  );
  const createInitiative = useCreateInitiativeThread(fixedProjectId);
  return (
    <SidebarInset className="isolate h-dvh min-h-0 overflow-hidden bg-background">
      <div className="flex h-full flex-col">
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4 sm:px-6">
          <SidebarHeaderTrigger className="size-7 shrink-0" />
          <div className="flex items-center gap-2">
            <BotIcon className="size-4 text-muted-foreground/70" />
            <h1 className="text-sm font-semibold">Agentes</h1>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {agents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay coordinadores asignados.{" "}
              <Link to="/settings" search={{ section: "projects" }}>
                Configurar proyectos
              </Link>
            </p>
          ) : (
            <div className="space-y-3">
              {agents.map((agent) => (
                <section key={agent.id} className="rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <AgentAvatar
                      agentId={agent.id}
                      displayName={agent.name}
                      avatarFileName={agent.avatarFileName}
                      size="md"
                    />
                    <div>
                      <div className="font-medium">{agent.name}</div>
                      <p className="text-sm text-muted-foreground">{agent.description}</p>
                      {projectAgentWarning(agent) ? (
                        <p className="text-xs text-muted-foreground">
                          {projectAgentWarning(agent)}
                        </p>
                      ) : null}
                    </div>
                    <button
                      onClick={() => void createInitiative(agent)}
                      className="ml-auto rounded border px-3 py-1 text-sm"
                    >
                      Nuevo chat
                    </button>
                  </div>
                  <div className="mt-3 space-y-1">
                    {initiativesForProjectAgent({
                      threads,
                      projectId: fixedProjectId,
                      agentId: agent.id,
                    }).map((thread) => (
                      <Link
                        key={thread.id}
                        to="/$threadId"
                        params={{ threadId: thread.id }}
                        className="flex justify-between text-sm hover:underline"
                      >
                        <span>{thread.title}</span>
                        <span className="text-muted-foreground">
                          {formatRelativeTime(thread.updatedAt ?? thread.createdAt)}
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </SidebarInset>
  );
}
